import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ITealCompClaim, TealCompClaim, ClaimAdjusterNotification } from '../../../models/teal-comp-claim';
import { CardModule } from 'primeng/card';
import { CarrierSearchComponent } from '../../carriers/carrier-search/carrier-search.component';
import { Carrier, CarrierInsert, CarrierUpdate, ICarrier } from '../../../models/carrier';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { CarrierEditorComponent } from '../../carriers/carrier-editor/carrier-editor.component';
import { ButtonModule } from 'primeng/button';
import { ClaimService } from '../../../services/claim.service';
import { NgIf } from '@angular/common';
import { Adjuster, AdjusterInsert, AdjusterUpdate, IAdjuster } from '../../../models/adjuster';
import { AdjusterEditorComponent } from '../../adjusters/adjuster-editor/adjuster-editor.component';
import { AdjusterSearchComponent } from '../../adjusters/adjuster-search/adjuster-search.component';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { CheckboxModule } from 'primeng/checkbox';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { FormsModule } from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { Store } from '@ngxs/store';
import { ReferralState } from '../../../state/referrals/referral-state';
import { TealCompReferralSubmission } from '../../../models/teal-comp-referral';
import { MessageService } from 'primeng/api';
import { InvalidLocationError, LocationService } from '@harlowe/core';
import { LocationInsert } from '@harlowe/core';

@Component({
  selector: 'app-claim-editor',
  standalone: true,
  imports: [CardModule,CarrierSearchComponent,OverlayPanelModule,CarrierEditorComponent,ButtonModule,
            NgIf,AdjusterEditorComponent, AdjusterSearchComponent, ButtonGroupModule, CheckboxModule,
            FloatLabelModule,InputTextModule, FormsModule, InputTextareaModule, CalendarModule
  ],
  templateUrl: './claim-editor.component.html',
  styleUrl: './claim-editor.component.scss'
})
export class ClaimEditorComponent
{
  @Input() claim: ITealCompClaim = {} as ITealCompClaim;
  @Output() saved: EventEmitter<ITealCompClaim> = new EventEmitter<ITealCompClaim>();
  @Output() cancelled = new EventEmitter();

  //Listen for whether the user has selected a referral to work with
  selectedReferral$ = this._store.select(ReferralState.currentReferral);
  activeSubmission?: TealCompReferralSubmission;
  showAdjuster2Search: boolean = false;
  showAdjusterSupervisorSearch: boolean = false;

  constructor(private _claimService: ClaimService, private _store: Store, private _messages: MessageService, private _locations: LocationService)
  {
    this.selectedReferral$.subscribe
    (
      referral =>
        {
          this.activeSubmission = referral?.submission;
        }
    )
  }

  carrierSelected(event: Carrier)
  {
    this.claim.carrier = event;
  }

  async carrierCreated(event: ICarrier)
  {
    try
    {
      //Add the location first
      var locId: number | undefined;
      if(event.companyLocation)
      {
        locId = (await this._locations.addLocation(LocationInsert.create(event.companyLocation))).id
      }

      this._claimService.createNewCarrier(CarrierInsert.create(event,locId))
      .then
      (
        c => this.claim.carrier = c
      )
    }
    catch(error)
    {
      if(error instanceof InvalidLocationError)
        this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could not Create Carrier', detail: 'Invalid Location'})
      else
        this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could not Create Carrier', detail: 'Unknown Error'})
    }
  }

  async carrierEdited(carrierId:number, event:ICarrier)
  {

    try
    {
      //Update the location first, and then make sure we include that in the update
      var locId: number | undefined;
      if(event.companyLocation)
      {
        locId = (await this._locations.addLocation(LocationInsert.create(event.companyLocation))).id
      }

      this._claimService.updateCarrier(carrierId,CarrierUpdate.create(event,locId) )
      .then
      (
        c => this.claim.carrier = c
      )

    }
    catch(error)
    {
      if(error instanceof InvalidLocationError)
        this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could not Update Carrier', detail: 'Invalid Location'})
      else
        this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could not Update Carrier', detail: 'Unknown Error'})
    }
  }

  adjusterSelected(event: Adjuster)
  {
    this.removeAdjusterNotificationForRole('adjuster');
    this.claim.adjuster = event;
  }

  adjusterCreated(event: IAdjuster)
  {
    this._claimService.createNewAdjuster(AdjusterInsert.create(event))
    .then
    (
      a => this.claim.adjuster = a
    )
  }

  adjusterEdited(adjusterId:number, event: IAdjuster)
  {
    this._claimService.updateAdjuster(adjusterId,AdjusterUpdate.create(event))
    .then
    (
      a => this.claim.adjuster = a
    )
  }

  adjuster2Selected(event: Adjuster)
  {
    this.removeAdjusterNotificationForRole('adjuster2');
    this.claim.adjuster2 = event;
  }

  adjuster2Created(event: IAdjuster)
  {
    this._claimService.createNewAdjuster(AdjusterInsert.create(event))
    .then
    (
      a => this.claim.adjuster2 = a
    )
  }

  adjuster2Edited(adjusterId:number, event: IAdjuster)
  {
    this._claimService.updateAdjuster(adjusterId,AdjusterUpdate.create(event))
    .then
    (
      a => this.claim.adjuster2 = a
    )
  }

  adjusterSupervisorSelected(event: Adjuster)
  {
    this.removeAdjusterNotificationForRole('adjusterSupervisor');
    this.claim.adjusterSupervisor = event;
  }

  adjusterSupervisorCreated(event: IAdjuster)
  {
    this._claimService.createNewAdjuster(AdjusterInsert.create(event))
    .then
    (
      a => this.claim.adjusterSupervisor = a
    )
  }

  adjusterSupervisorEdited(adjusterId:number, event: IAdjuster)
  {
    this._claimService.updateAdjuster(adjusterId,AdjusterUpdate.create(event))
    .then
    (
      a => this.claim.adjusterSupervisor = a
    )
  }

  isAdjusterNotified(adjuster?: Adjuster) : boolean
  {
    let ids = (this.claim as TealCompClaim).adjustersToNotifyIds;
    if (ids)
    { 
           return !!adjuster && ids.includes(adjuster.id);
    }
    return false;
  }

  setAdjusterNotification(adjuster: Adjuster | undefined, checked: boolean)
  {
    if(!adjuster)
      return;

    const currentIds = (this.claim as TealCompClaim).adjustersToNotifyIds;
    let newIds: number[];

    if(checked)
    {
      if(!currentIds.includes(adjuster.id))
        newIds = [...currentIds, adjuster.id];
      else
        newIds = currentIds;
    }
    else
    {
      newIds = currentIds.filter((id: number) => id != adjuster.id);
    }

    // Update AdjustersToNotify with the new list
    this.claim.adjustersToNotify = newIds.map(id => ({ adjusterId: id, claimId: this.claim.id }));
  }

  private getAdjusterByRole(role: 'adjuster' | 'adjuster2' | 'adjusterSupervisor') : Adjuster | undefined
  {
    return role == 'adjuster'
      ? this.claim.adjuster
      : role == 'adjuster2'
        ? this.claim.adjuster2
        : this.claim.adjusterSupervisor;
  }

  private removeAdjusterNotificationForRole(role: 'adjuster' | 'adjuster2' | 'adjusterSupervisor')
  {
    const adjuster = this.getAdjusterByRole(role);
    if(adjuster?.id && this.claim.adjustersToNotify)
      this.claim.adjustersToNotify = this.claim.adjustersToNotify.filter(n => n.adjusterId != adjuster.id);
  }

  unassignAdjuster(role: 'adjuster' | 'adjuster2' | 'adjusterSupervisor')
  {
    this.removeAdjusterNotificationForRole(role);

    if(role == 'adjuster')
      this.claim.adjuster = undefined;
    else if(role == 'adjuster2')
      this.claim.adjuster2 = undefined;
    else
      this.claim.adjusterSupervisor = undefined;
  }

  allowSave() : boolean
  {
    return this.claim.carrier != null && this.claim.adjuster != null && this.claim.claimNumber != null 
    && this.claim.bodyPart != null && this.claim.bodyPart != ""
    && this.claim.accidentDescription != null && this.claim.accidentDescription != ""
    && this.claim.injuryDescription != null && this.claim.injuryDescription != "";
  }

  saveButtonPressed(event: MouseEvent)
  {
    this.saved.emit(this.claim);
  }

  cancelButtonPressed(event: MouseEvent)
  {
    this.cancelled.emit();
  }

  fillFromSubmission()
  {
    if(this.activeSubmission)
    {

      this.claim.claimNumber = this.activeSubmission.claimNumber;
      this.claim.bodyPart = this.activeSubmission.bodyPart + " Side: " + this.activeSubmission.bodySide;
      this.claim.claimDate = new Date(this.activeSubmission.dateOfInjury);
      this.claim.injuryDescription = this.activeSubmission.injuryDescription;
      this.claim.accidentDescription = this.activeSubmission.accidentDescription;
    }
  }

  clearForm()
  {
    this.claim = {} as ITealCompClaim;
  }

}
